import { CaptureOption, MediaType, TrackEvent } from '../enums';
import { getMicrophones, deviceDetector, DeviceChangeEvent, DeviceInfo, isSameDefaultMicrophone } from '../common/device-detector';
import { LocalTrack } from './local-track';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT from '../inner-emitter/inner-event';
import { MICROPHONE_DEFAULT } from '../common/constants';
import { AudioManager } from '../manager/audio-manager';
import { isUndefined, performanceNow } from '../utils/utils';
import { AudioNodeConnector, AudioSource, MixAudioPipeline, createAudioNode } from '../manager/audio-mixer';
import { AudioCodecPipelineType, LocalAudioPlayer, MULTI_VIDEO_DATA_TYPE, MonitorEventId, RecaptureMode, addMonitorEvent, getNetworkTime } from '..';
import kvStatManager, { KV_REPORT_KEY_AUDIO_CAPTURE, KV_REPORT_KEY_AUDIO_MANAGER, KV_REPORT_VALUE_AUDIO_MANAGER_MIX } from '../manager/kv-stat-manager';
import { CHROME_MAJOR_VERSION, IS_ANY_SAFARI, IS_FIREFOX, IS_IOS, IS_SAFARI } from '../utils/environment';
import VolumeMeter from '../common/volume-meter/volume-meter';
import { LONG_SUSPENDED_THRESHOLD, getAudioContext, resumeContext } from '../utils/audio-context';
export class LocalAudioTrack extends LocalTrack<'audio'> {
  declare player: LocalAudioPlayer;
  mediaType = MediaType.AUDIO;
  volume = 0;
  profile: {
    echoCancellation: boolean | string;
    autoGainControl: boolean;
    noiseSuppression: boolean;
    sampleRate: number;
    channelCount: number;
    bitrate: number;
  } = {
      echoCancellation: true,
      autoGainControl: true,
      noiseSuppression: true,
      sampleRate: 48000,
      channelCount: 1,
      bitrate: 40 // kbps
    };
  playerMuted = true;
  pipeline: MixAudioPipeline;
  earMonitorGainNode = new AudioNodeConnector<GainNode>();
  private _output = new AudioNodeConnector<AudioDestinationNode>();

  codecPipeline: (((frame: RTCEncodedAudioFrame, mediaType: MediaType) => RTCEncodedVideoFrame) | undefined)[] = [];
  stat: LocalAudioStat = {
    bytesSent: 0,
    packetsSent: 0,
    audioLevel: 0,
    totalAudioEnergy: 0
  };
  // 0 - 100
  get dbVolume() {
    return VolumeMeter.isRunning ? this.player.pipeline.volumeMeter.getVolumeDb() : Math.floor(Math.max(10 * Math.log10(this.volume) + 100, 0));
  }
  // TODO：@leochliu 改个名字，目前的名字容易被误认为混音的音频 map
  mixedAudioReferenceMap = new Map<string, [AudioNodeConnector, AudioNodeConnector<GainNode>]>(); // 已混合的回声消除播放参考信号
  isAudioContextLongSuspended = false;
  after3aSilenceStartTime = 0;
  constructor(am: AudioManager) {
    super(MediaType.AUDIO, LocalAudioPlayer, am.room);
    this.manager = am;
    this.pipeline = new MixAudioPipeline(am);
    this.pipeline.source.pipeTo(this.player.pipeline.volumeMeter);
    this.pipeline.gain.pipeTo(this.earMonitorGainNode).pipeTo(this._output);
    this.pipeline.gain.pipeTo(this.player.pipeline.volumeMeterAfter3A);
    this._volumeDetectionSource.pipeTo(this.player.pipeline.volumeMeter);
    // 避免多实例没有正常注册事件监听
    this.handleMicrophoneAdded = this.handleMicrophoneAdded.bind(this);
    this.handleMicrophoneRemoved = this.handleMicrophoneRemoved.bind(this);
    innerEmitter.on(INNER_EVENT.AUDIO_CONTEXT_LONG_SUSPENDED, this.handleAudioContextLongSuspended, this);
  }
  getAudioLevel(): number {
    const result = (this.volume || super.getAudioLevel()) * this.captureVolume;
    return result > 1 ? 1 : result;
  }
  // 获取 3A 处理后的 0.00000 未丢失精度的音量数据. 未开启 3A 时，返回 undefined
  getInternalAudioLevelAfter3A() {
    if (this.pipeline.isProcessEnabled) return this.player.getInternalAudioLevelAfter3A();
    return void 0;
  }
  // 更新无声时间戳, 用于统计退房前的无声持续时间
  updateAfter3aSilenceStartTime(volume?: number) {
    if (isUndefined(volume)) return;
    if (volume === 0 && !this.after3aSilenceStartTime) {
      this.after3aSilenceStartTime = performanceNow();
    } else if (volume > 0) {
      this.after3aSilenceStartTime = 0;
    }
  }
  async setInputMediaStreamTrack(track: MediaStreamAudioTrack) {
    const settings = this.trackSettings || {};
    kvStatManager.addEnum({ key: KV_REPORT_KEY_AUDIO_CAPTURE.MICROPHONE_CAHNNELS, value: settings.channelCount || 0, useUV: false });
    kvStatManager.addEnum({ key: KV_REPORT_KEY_AUDIO_CAPTURE.MICROPHONE_SAMPLERATE, value: settings.sampleRate || 0, useUV: false });
    kvStatManager.addEnum({ key: KV_REPORT_KEY_AUDIO_MANAGER.MIX, value: KV_REPORT_VALUE_AUDIO_MANAGER_MIX.MICROPHONE });
    const { sampleRate, channelCount } = settings;
    this._log.info(`local audio track input ${JSON.stringify({ sampleRate, channelCount })}`);
    this.pipeline.source.channelCount = channelCount || 1;
    this.pipeline.replaceSource(track);
    await super.setInputMediaStreamTrack(track);
    this.updatePlayingState(!!track);
  }
  async capture({ deviceId, customSource, useExactDeviceId = true, retryWhenExactFailed }: CaptureOption, stopAfterCaptured = false) {
    const mediaStream = await super.capture({
      video: false,
      audio: true,
      microphoneId: deviceId,
      echoCancellation: this.profile.echoCancellation,
      autoGainControl: this.profile.autoGainControl,
      noiseSuppression: this.profile.noiseSuppression,
      sampleRate: this.profile.sampleRate,
      channelCount: this.profile.channelCount,
      useExactDeviceId,
      retryWhenExactFailed,
      customSource
    }, stopAfterCaptured);
    // 麦克风采集成功后，调用 resume 可以让 audioContext running。即使用户没有点击页面。
    resumeContext();
    return mediaStream;
  }

  async switchDevice(deviceId: string) {
    if (!this.mediaTrack) return;
    if (this.deviceId === deviceId && !this.isUseCustomSource) {
      if (deviceId === MICROPHONE_DEFAULT) {
        // 切换 default 麦克风，判断是否是相同的 default 麦克风
        const isSameDefaultMic = await isSameDefaultMicrophone(this.groupId, this.label);
        if (isSameDefaultMic) return;
      } else {
        return;
      }
    }
    try {
      this.log.info(`switchDevice audio to: ${deviceId}`);
      // 自定义采集的 track 不需要 stop
      if (this.sourceTrack) {
        this.sourceTrack.stop();
      }
      await this.capture({ deviceId, useExactDeviceId: true, retryWhenExactFailed: false });
      innerEmitter.emit(INNER_EVENT.SWITCH_DEVICE_SUCCESS, { track: this });
      this.log.info('switch microphone success');
    } catch (error) {
      this.log.error(`switch microphone failed ${error}`);
      if (this.deviceId) {
        this.recapture(this.deviceId);
      }
      throw error;
    }
  }

  listenDeviceChange() {
    if (deviceDetector && !deviceDetector.listeners(DeviceChangeEvent.AUDIO_INPUT_REMOVED).includes(this.handleMicrophoneRemoved)) {
      deviceDetector.on(DeviceChangeEvent.AUDIO_INPUT_REMOVED, this.handleMicrophoneRemoved, this);
    }
  }

  async handleMicrophoneRemoved(device: DeviceInfo) {
    if (device.deviceId === this.deviceId) {
      let isWaitNewDevice = this.recaptureMode === RecaptureMode.WAIT_CURRENT_DEVICE;
      this.log.warn(`RecaptureMode: ${RecaptureMode[this.recaptureMode]}. Current microphone is lost: ${JSON.stringify(device)}`);
      if (this.recaptureMode === RecaptureMode.AUTO_SWITCH_NEW_DEVICE) {
        addMonitorEvent(this.userId, { eventId: MonitorEventId.DEVICE_CAPTURE, param1: 6, streamType: MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUDIO });
        const microphones = await getMicrophones();
        if (microphones[0]) {
          this.recapture(microphones[0].deviceId);
        } else {
          isWaitNewDevice = true; // 设备拔出后，没可用麦克风设备，则等待插入麦克风再恢复采集。
        }
      }
      if (isWaitNewDevice) {
        deviceDetector!.on(DeviceChangeEvent.AUDIO_INPUT_ADDED, this.handleMicrophoneAdded, this);
      }
    }
  }

  // 麦克风拔出后，没可用麦克风设备，等待插入麦克风再恢复采集。
  handleMicrophoneAdded(device: DeviceInfo) {
    if (this.recaptureMode === RecaptureMode.WAIT_CURRENT_DEVICE && device.deviceId !== this.deviceId) return;
    deviceDetector!.off(DeviceChangeEvent.AUDIO_INPUT_ADDED, this.handleMicrophoneAdded, this);
    this.log.warn(`microphone added: ${JSON.stringify(device)}`);
    this.recapture(device.deviceId);
  }

  async update3A({ echoCancellation, noiseSuppression, autoGainControl }: { echoCancellation?: string | boolean; noiseSuppression?: boolean; autoGainControl?: boolean; }) {
    const track = this.sourceTrack || this.mediaTrack;
    if (!track) return;
    const constraints = track.getConstraints();
    let is3AChanged = false;
    if (!isUndefined(echoCancellation) && echoCancellation !== this.profile.echoCancellation) {
      this.profile.echoCancellation = echoCancellation;
      constraints.echoCancellation = echoCancellation;
      is3AChanged = true;
    }
    if (!isUndefined(noiseSuppression) && noiseSuppression !== this.profile.noiseSuppression) {
      this.profile.noiseSuppression = noiseSuppression;
      constraints.noiseSuppression = noiseSuppression;
      is3AChanged = true;
    }
    if (!isUndefined(autoGainControl) && autoGainControl !== this.profile.autoGainControl) {
      this.profile.autoGainControl = autoGainControl;
      constraints.autoGainControl = autoGainControl;
      is3AChanged = true;
    }
    if (!is3AChanged) return;
    // chrome 不支持 applyConstraints 动态更新 3A, 通过重新采集规避. ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/102039602212372878
    if (IS_FIREFOX || IS_SAFARI) {
      await track.applyConstraints(constraints).catch(error => this._log.warn('update3A failed: ', error));
    } else if (this.deviceId) {
      await this.recapture(this.deviceId, true);
    }
  }
  get captureVolume() {
    return this.pipeline.volume;
  }
  setCaptureVolume(volume: number) {
    this.pipeline.setVolume(volume / 100);
    if (this.pipeline.gain.node) {
      kvStatManager.addEnum({ key: KV_REPORT_KEY_AUDIO_MANAGER.MIX, value: KV_REPORT_VALUE_AUDIO_MANAGER_MIX.GAIN });
    }
  }
  muteMicOnly = false;
  private _volumeDetectionTrack: MediaStreamAudioTrack | null = null;
  private _volumeDetectionSource = new AudioNodeConnector();
  /**
   * Set mute state for the local audio track.
   * @param mute - Mute state:
   *   - `true`: Mute all audio (microphone and mixed audio).
   *   - `false`: Unmute all audio.
   *   - `'microphone'`: Only mute the microphone input. Mixed audio (if any) will still be sent.
   *     If there is no mixed audio, this behaves the same as `true`.
   * @param keepVolumeDetection - When true, clones the sourceTrack for volume detection
   *   while muting the upstream audio. Only takes effect when mute is `true` or `'microphone'`.
   */
  setMute(mute: boolean | 'microphone', keepVolumeDetection?: boolean) {
    // Clean up previous volume detection track if exists
    this._cleanupVolumeDetectionTrack();

    if (mute === 'microphone') {
      // Only mute the microphone input, keep mixed audio active
      this.muteMicOnly = true;
      // Mute the source track (microphone input)
      if (this.sourceTrack) this.sourceTrack.enabled = false;

      // Setup volume detection with cloned track if needed
      if (keepVolumeDetection) {
        this._setupVolumeDetectionTrack();
      }

      if (this.manager?.isAudioMixed) {
        // Has mixed audio, only mute microphone, keep output active for mixed audio
        // Don't change muted state or emit mute event since audio is still being sent
        if (this._outputTrack) this._outputTrack.enabled = true;
      } else {
        // If no mixed audio sources, behave like full mute
        this.muted = true;
        if (this._inputTrack) this._inputTrack.enabled = false;
        if (this._outputTrack) this._outputTrack.enabled = false;
      }
      this.emit(TrackEvent.MUTE, this);
      innerEmitter.emit(INNER_EVENT.TRACK_MUTED, { track: this });
    } else if (mute === true) {
      this.muteMicOnly = false;
      this.muted = true;
      // Mute all tracks
      if (this.sourceTrack) this.sourceTrack.enabled = false;
      if (this._inputTrack) this._inputTrack.enabled = false;
      if (this._outputTrack) this._outputTrack.enabled = false;

      // Setup volume detection with cloned track if needed
      if (keepVolumeDetection) {
        this._setupVolumeDetectionTrack();
      }

      this.emit(TrackEvent.MUTE, this);
      innerEmitter.emit(INNER_EVENT.TRACK_MUTED, { track: this });
    } else {
      this.muteMicOnly = false;
      this.muted = false;
      // Restore source track state
      if (this.sourceTrack) this.sourceTrack.enabled = true;
      if (this._inputTrack) this._inputTrack.enabled = true;
      if (this._outputTrack) this._outputTrack.enabled = true;
      this.emit(TrackEvent.UNMUTE, this);
      innerEmitter.emit(INNER_EVENT.TRACK_UNMUTED, { track: this });
    }
  }

  /**
   * Setup a cloned track for volume detection when muted.
   */
  private _setupVolumeDetectionTrack() {
    const trackToClone = this.sourceTrack || this.mediaTrack;
    if (!trackToClone) return;

    // Clone the track for volume detection
    this._volumeDetectionTrack = trackToClone.clone() as MediaStreamAudioTrack;
    this._volumeDetectionTrack.enabled = true;

    // Connect the cloned track to volume meter
    const audioNode = createAudioNode(this._volumeDetectionTrack);
    if (audioNode) {
      this._volumeDetectionSource.setNode(audioNode);
    }
  }

  /**
   * Cleanup the volume detection track.
   */
  private _cleanupVolumeDetectionTrack() {
    if (this._volumeDetectionTrack) {
      this._volumeDetectionTrack.stop();
      this._volumeDetectionTrack = null;
    }
    this._volumeDetectionSource.deleteNode();
  }
  setAudioVolume(volume: number) {
    super.setAudioVolume(0); // 耳返声音不用 element 播放, 设置为 0
    if (IS_IOS) {
      this.player.setMuted(true); // IOS 无法设置 volume
    }
    if (!this.earMonitorGainNode.node) {
      this.earMonitorGainNode.setNode(getAudioContext().createGain());
      this._output.setNode(getAudioContext().destination);
    }
    this.earMonitorGainNode.node!.gain.value = volume;
  }
  enableTrackANS(enable: boolean) {
    return this.update3A({ noiseSuppression: enable });
  }
  enableTrackAEC(enable: boolean) {
    if (!this.sourceTrack) return;
    // 1. ios safari 18.4 关闭 echoCancellation 会导致采集音量降到很低 2. mac safari 18.3 关闭 echoCancellation 会导致右声道无声
    if (IS_SAFARI || IS_IOS) return;
    // chrome 不支持动态更新 3a, 需要重新采集: https://issues.chromium.org/issues/390804131
    return this.update3A({ echoCancellation: enable });
  }
  addDenoiser(node: AudioNode) {
    if (CHROME_MAJOR_VERSION <= 92 && this.trackSettings?.sampleRate !== 48000) {
      this._log.warn('denoiser only support sampleRate 48000 before chrome 93');
      return;
    }
    kvStatManager.addEnum({ key: KV_REPORT_KEY_AUDIO_MANAGER.MIX, value: KV_REPORT_VALUE_AUDIO_MANAGER_MIX.DENOISER });
    this.pipeline.denoiser.setNode(node);
    this.enableTrackANS(false);
    // 开启降噪后，使用 audio 标签播放的耳返声音会变调。使用 context 的 destination 播放来规避
    // ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022121445486
  }
  mixAudioReference(source: AudioSource, sourceId: string) {
    if (this.mixedAudioReferenceMap.has(sourceId)) return;
    this.log.info(`mixAudioReference() => ${sourceId}`);
    const sourceNode = createAudioNode(source);
    if (!sourceNode) return;
    const sourceNodeConnector = new AudioNodeConnector();
    const gainNode = getAudioContext().createGain();
    gainNode.gain.value = 1;
    const gainNodeConnector = new AudioNodeConnector<GainNode>();
    sourceNodeConnector.pipeTo(gainNodeConnector).pipeTo(this.pipeline.mixNode);
    sourceNodeConnector.setNode(sourceNode);
    gainNodeConnector.setNode(gainNode);
    this.mixedAudioReferenceMap.set(sourceId, [sourceNodeConnector, gainNodeConnector]);
  }
  unMixAudioReference(sourceId: string) {
    const [sourceConnector, gainNodeConnector] = this.mixedAudioReferenceMap.get(sourceId) || [];
    if (!sourceConnector) return;
    this.log.info(`unMixAudioReference() => ${sourceId}`);
    sourceConnector.deleteNode();
    gainNodeConnector?.deleteNode();
    this.mixedAudioReferenceMap.delete(sourceId);
  }
  setAudioReferenceVolume(sourceId: string, volume: number) {
    const [sourceConnector, gainNodeConnector] = this.mixedAudioReferenceMap.get(sourceId) || [];
    if (!gainNodeConnector?.node) return;
    gainNodeConnector.node.gain.value = volume / 100;
    this.log.info(`setAudioReferenceVolume() => ${sourceId} ${gainNodeConnector.node.gain.value}`);
  }
  addAudioProcessor(localAudioWorkletNode: AudioNode, mixNode: AudioNode, silentNode: AudioNode) {
    this.pipeline.silentNode.setNode(silentNode);
    this.pipeline.mixNode.setNode(mixNode);
    this.pipeline.aec.setNode(localAudioWorkletNode);
  }
  removeDenoiser(node: AudioNode) {
    if (this.pipeline.denoiser.node === node) {
      this.pipeline.denoiser.deleteNode();
      return this.enableTrackANS(true);
    }
  }
  removeAudioProcessor(localAudioWorkletNode?: AudioNode) {
    if (this.pipeline.aec.node === localAudioWorkletNode) {
      this.pipeline.aec.deleteNode();
      this.pipeline.silentNode.deleteNode();
      this.pipeline.mixNode.deleteNode();
    }
  }
  close() {
    this._cleanupVolumeDetectionTrack();
    this.mixedAudioReferenceMap.forEach(([sourceConnector, gainNodeConnector]) => {
      sourceConnector.deleteNode();
      gainNodeConnector.deleteNode();
    });
    this.mixedAudioReferenceMap.clear();
    this.pipeline.remove();
    this.earMonitorGainNode.deleteNode();
    this._output.deleteNode();
    deviceDetector!.off(
      DeviceChangeEvent.AUDIO_INPUT_ADDED,
      this.handleMicrophoneAdded,
      this
    );
    deviceDetector!.off(
      DeviceChangeEvent.AUDIO_INPUT_REMOVED,
      this.handleMicrophoneRemoved,
      this
    );
    innerEmitter.off(INNER_EVENT.AUDIO_CONTEXT_LONG_SUSPENDED, this.handleAudioContextLongSuspended, this);
    super.close();
  }

  async recapture(deviceId: string, stopAfterCaptured = false): Promise<void> {
    try {
      await super.recapture(deviceId, stopAfterCaptured);
    } catch (error) {
      const otherMicrophone = (await getMicrophones()).find(microphone => microphone.deviceId !== deviceId);
      if (otherMicrophone) {
        await super.recapture(otherMicrophone.deviceId);
      } else {
        throw error;
      }
    }
  }
  encodeFrame(frame: RTCEncodedAudioFrame) {
    if (!this.manager) return frame;
    return this.manager.encodePipeline.reduceRight((frame, encoder) => (encoder ? encoder({ frame, ntp: getNetworkTime() }) : frame), frame);
  }
  get enableEncodeFrame() {
    if (!this.manager) return false;
    return this.manager.encodePipeline.some(x => x);
  }
  get enableEncryptFrame() {
    return this.manager && !!this.manager.encodePipeline[AudioCodecPipelineType.ENCRYPT_AND_DECRYPT];
  }
  // ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022125677809
  // 规避 AI 降噪 + Audio Context 长时间 suspend 导致的无声问题
  handleAudioContextLongSuspended({ isSuspended }: { isSuspended: boolean }) {
    if (!this.pipeline.isProcessEnabled) return;
    if (isSuspended) {
      this.isAudioContextLongSuspended = true;
      this.log.warn(`context has suspended for ${LONG_SUSPENDED_THRESHOLD / 1000} seconds, change to source audio${IS_ANY_SAFARI ? '' : ', non-Safari'}`);
      const sourceTrack = this.sourceTrack || this.mediaTrack;
      if (sourceTrack) this.setOutputMediaStreamTrack(sourceTrack);
    } else {
      this.isAudioContextLongSuspended = false;
      this.log.warn('context has resumed, change to processed audio');
      if (this.pipeline.track) this.setOutputMediaStreamTrack(this.pipeline.track);
    }
  }
  setOutputMediaStreamTrack(track: MediaStreamAudioTrack) {
    // 切换麦克风后, 如果 AudioContext 仍 suspend, 切换到原始采集的音频来规避无声问题
    if (this.isAudioContextLongSuspended) {
      const sourceTrack = this.sourceTrack || this.mediaTrack;
      if (sourceTrack) track = sourceTrack;
    }
    super.setOutputMediaStreamTrack(track);
    if (this.muteMicOnly && this.manager?.isAudioMixed) {
      track.enabled = true;
    }
  }

  onAudioMixedChanged(isAudioMixed: boolean) {
    this.log.info(`onAudioMixedChanged() isAudioMixed: ${isAudioMixed}`);
    if (!this.muteMicOnly) return;
    if (isAudioMixed) {
      // 混音开始：麦克风已 mute 但有混音，需要恢复 output 让混音音频继续发送
      this.muted = false;
      if (this._outputTrack && this._outputTrack !== this._inputTrack) {
        this._outputTrack.enabled = true;
      }
    } else {
      // 混音结束：仅麦克风静音模式下，没有混音了，等同于完全静音
      this.muted = true;
      // if (this._localAudioTrack._inputTrack) this._localAudioTrack._inputTrack.enabled = false;
      // if (this._localAudioTrack._outputTrack) this._localAudioTrack._outputTrack.enabled = false;
    }
    this.emit(TrackEvent.MUTE, this);
    innerEmitter.emit(INNER_EVENT.TRACK_MUTED, { track: this });
  }
}
